import { Box, Grid, List, Paper, Typography, useTheme } from "@mui/material";
import { gridClasses, GridColDef } from "@mui/x-data-grid";
import { GetFieldLookupItem, useFormMetadata, useLookups } from "@ngt/forms";
import { DateTime } from "luxon";
import { useContext, useMemo } from "react";
import { Link, Outlet, useNavigate, useParams } from "react-router-dom";
import { ContractSummary, FormDefinitions, LotNote, Permissions } from "../api/dtos";
import { DetailsContent, DetailsHeader, DetailsPanel, GridColumn, GridData, GridFillerRow, GridLabel, GridRow } from "../components/DetailsPanel";
import GridActions from "../components/GridActions";
import LotAttachments from "../components/LotAttachments";
import { LotHistory } from "../components/LotHistory";
import { StyledActionButton, StyledButton } from "../components/StyledButton";
import CollapsibleTablePremium from "../components/tables/CollapsibleTablePremium";
import TitleBox from "../components/TitleBox";
import { createCustomToolbarProps } from "../components/utility/CustomToolbarProps";
import PermissionsContext from "../contexts/auth/PermissionsContext";
import LotContext from "../contexts/data/LotContext";
import ProjectContext from "../contexts/data/ProjectContext";
import StageContext from "../contexts/data/StageContext";
import LotAttachmentsContext from "../contexts/LotAttachmentsContext";
import LotChangesContext from "../contexts/LotChangesContext";
import LotDetailsContext from "../contexts/LotDetailsContext";
import LotNotesContext from "../contexts/LotNotesContext";
import LotPropertiesContext from "../contexts/LotPropertiesContext";
import useAttachmentsByLotId from "../hooks/data/useAttachmentsByLotId";
import useContractSummariesByLotId from "../hooks/data/useContractSummariesByLotId";
import useLotChangesByLotId from "../hooks/data/useLotChangesByLotId";
import useLotDetailsByProjectIdStageIdLotId from "../hooks/data/useLotDetailsByProjectIdStageIdLotId";
import useLotNotesByLotId from "../hooks/data/useLotNotesByLotId";
import ContractSummariesContext from "../contexts/ContractSummariesContext";

const useContractColumns = () => {
    const metadata = useFormMetadata(FormDefinitions.Contract);
    const routeParams = useParams();

    const {data: permissions} = useContext(PermissionsContext);

    const {data: lookups, loading: _lookupsLoading} = useLookups(metadata);

    const projectId = routeParams.projectId;
    const stageId = routeParams.stageId;
    const lotId = routeParams.lotId;

    const contractColumns = useMemo(() => {
        const columns: GridColDef<ContractSummary>[] =  [
            {
                field: "status",
                headerName: "Contract Status",
                valueGetter: (_value, contract) => GetFieldLookupItem(lookups, 'Status', contract.status)?.value,
                width: 180
            },
            {
                field: "price",
                headerName: "Contract Price",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 150,
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                }
            },
            {
                field: "exchangeDate",
                headerName: "Exchange Date",
                type: "date",
                width: 150,
                //flex: 1,
                valueFormatter: (_value, row) => {
                    return row.exchangeDate ? DateTime.fromISO(row.exchangeDate).toFormat("dd/MM/yyyy") : undefined;
                }
            },
            {
                field: "rateAdjustments",
                headerName: "Rate Adjustments",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 150,
                //flex: 1,
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                }
            },
            {
                field: "legalFees",
                headerName: "Legal Fees",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 150,
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                }
            },
            {
                field: "commission",
                headerName: "Commission",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 150,
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                }
            },
            {
                field: "halfShareInterest",
                headerName: "Half Share Interest",
                type: "number",
                headerAlign: "left",
                align: "left",
                width: 150,
                //flex: 1,
                renderCell: (params) => {
                    return params.value ? Intl.NumberFormat("en-AU", {
                        style: "currency",
                        currency: "AUD"
                    }).format(params.value) : undefined;
                }
            },
            {
                field: "agentName",
                headerName: "Agent",
                flex: 1
            },
            {
                field: "settlementDate",
                headerName: "Settlement Date",
                type: "date",
                width: 150,
                //flex: 1,
                valueFormatter: (_value, row) => {
                    return row.settlementDate ? DateTime.fromISO(row.settlementDate).toFormat("dd/MM/yyyy") : undefined;
                }
            },
            
        ];

        if(permissions?.includes(Permissions.LotCanEditContract) || permissions?.includes(Permissions.LotCanDeleteContract)) {
            columns.push(
            {
                field: "actions",
                headerName: '',
                width: 100,
                renderCell: (params) => {
                    return (
                        <GridActions 
                            editPermission={Permissions.LotCanEditContract}
                            // deletePermission={Permissions.LotCanDeleteContract}
                            editUrl={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/contracts/edit/${params.row.id}`}
                            // deleteUrl={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/contracts/delete/${params.row.id}`}
                        />
                    )
                },
                disableExport: true
            });
        }

        return columns;
    }, [lookups, permissions]);

    return contractColumns;
}

const useLotNoteColumns = () => {
    const {data: permissions} = useContext(PermissionsContext);
    const metadata = useFormMetadata(FormDefinitions.LotNote);
    const routeParams = useParams();

    const {data: lookups, loading: _lookupsLoading} = useLookups(metadata);

    const projectId = routeParams.projectId;
    const stageId = routeParams.stageId;
    const lotId = routeParams.lotId;

    const lotNoteColumns = useMemo(() => {
        const columns: GridColDef<LotNote>[] =  [
            {
                field: "noteText",
                headerName: "Note Text",
                flex: 1 
            },
            {
                field: "enteredBy",
                headerName: "Entered By",
                width: 200
            }
        ]

        if(permissions?.includes(Permissions.LotCanEditNote) || permissions?.includes(Permissions.LotCanDeleteNote)) {
            columns.push(
            {
                field: "actions",
                headerName: '',
                width: 100,
                renderCell: (params) => {
                    return (
                        <GridActions 
                            editPermission={Permissions.LotCanEditNote}
                            deletePermission={Permissions.LotCanDeleteNote}
                            editUrl={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/notes/edit/${params.row.id}`}
                            deleteUrl={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/notes/delete/${params.row.id}`}
                        />
                    )
                },
                disableExport: true
            });
        }
        return columns;
    }, [lookups, permissions]);

    return lotNoteColumns;
}

export const LotDetails = () => {
    
    const lotMetadata = useFormMetadata(FormDefinitions.Lot);

    const {data: permissions} = useContext(PermissionsContext);
    
    const {data: lotLookups, loading: _lookupsLoading} = useLookups(lotMetadata);

    const theme = useTheme();
    const navigate = useNavigate();

    const routeParams = useParams();

    const contractColumns = useContractColumns();
    const lotNoteColumns = useLotNoteColumns();

    const projectId = Number(routeParams.projectId);
    const stageId = Number(routeParams.stageId);
    const lotId = Number(routeParams.lotId);
    
    if(!lotId) {
        navigate(`/projects/${projectId}/stages/${stageId}`);
    }

    const {data: project} = useContext(ProjectContext);
    const {data: stage} = useContext(StageContext);
    const {data: lot} = useContext(LotContext);

    const lotDetailsContextData = useLotDetailsByProjectIdStageIdLotId(projectId, stageId, lotId);
    const {data: lotData, loading: _lotLoading, refresh: refreshLots} = lotDetailsContextData;

    const lotPropertiesContextData = useContext(LotPropertiesContext);

    const contractSummariesContextData = useContractSummariesByLotId(lotId);
    const {data: contractSummaries, loading: contractSummariesLoading} = contractSummariesContextData;

    const lotNotesContextData = useLotNotesByLotId(lotId);
    const {data: lotNotes, loading: lotNotesLoading} = lotNotesContextData;
    
    const lotAttachmentsContextData = useAttachmentsByLotId(lotId);

    const lotChangesContextData = useLotChangesByLotId(lotId); 

    const contractsCustomToolbarProps = useMemo(() => createCustomToolbarProps({
        exportTitle: `${project?.name} - ${stage?.name} - Lot ${lot?.number}`,
        fileName: `${project?.name} - ${stage?.name} - Lot ${lot?.number}`,
        includeDateTime: true
    }), [project, stage, lot]);   
    
    const notesCustomToolbarProps = useMemo(() => createCustomToolbarProps({
        exportTitle: `${project?.name} - ${stage?.name} - Lot ${lot?.number} - Notes`,
        fileName: `${project?.name} - ${stage?.name} - Lot ${lot?.number} - Notes`,
        includeDateTime: true
    }), [project, stage, lot]);   

    return (
        <>
            <LotDetailsContext.Provider value={lotDetailsContextData}>
                <LotChangesContext.Provider value={lotChangesContextData}>
                    <LotPropertiesContext.Provider value={{ ...lotPropertiesContextData, refresh: refreshLots }} >
                        <LotNotesContext.Provider value={lotNotesContextData}>
                            <LotAttachmentsContext.Provider value={lotAttachmentsContextData}>
                                <ContractSummariesContext.Provider value={contractSummariesContextData}>
                                    <Outlet />
                                    
                                    <TitleBox>
                                        <Typography
                                            variant="h2"
                                        >
                                            {lotData?.projectName ? `${lotData?.projectName} - ` : undefined}
                                            {lotData?.stageName ? `${lotData?.stageName} - ` : undefined}
                                            {lotData?.lotNo ? `Lot ${lotData?.lotNo}` : undefined}
                                        </Typography>
                                        <Grid
                                            container
                                            gap={theme.spacing(2)}
                                            justifyContent={"end"}
                                        >
                                            
                                            {
                                                permissions?.includes(Permissions.StageCanEditLot) &&
                                                <StyledButton 
                                                        variant="contained"
                                                        //@ts-expect-error
                                                        component={Link}
                                                        to={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/edit`}
                                                        state={{
                                                            back: true
                                                        }}
                                                >
                                                    Edit Lot
                                                </StyledButton>
                                            }
                                            {
                                                permissions?.includes(Permissions.LotCanAddContract) &&
                                                <StyledButton 
                                                        variant="contained"
                                                        //@ts-expect-error
                                                        component={Link}
                                                        to={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/contracts/create`}
                                                        state={{
                                                            back: true
                                                        }}
                                                >
                                                    Add Contract
                                                </StyledButton>
                                            }
                                            {
                                                permissions?.includes(Permissions.LotCanAddProperty) &&
                                                <StyledButton 
                                                        variant="contained"
                                                        //@ts-expect-error
                                                        component={Link}
                                                        to={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/properties/create`}
                                                        state={{
                                                            back: true
                                                        }}
                                                >
                                                    Add Lot Property
                                                </StyledButton>
                                            }
                                            {
                                                permissions?.includes(Permissions.LotCanAddNote) &&
                                                <StyledButton 
                                                        variant="contained"
                                                        //@ts-expect-error
                                                        component={Link}
                                                        to={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/notes/create`}
                                                        state={{
                                                            back: true
                                                        }}
                                                >
                                                    Add Note
                                                </StyledButton>
                                            }
                                            {
                                                permissions?.includes(Permissions.LotCanAddAttachment) &&
                                                <StyledButton 
                                                        variant="contained"
                                                        //@ts-expect-error
                                                        component={Link}
                                                        to={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/attachments/create`}
                                                        state={{
                                                            back: true
                                                        }}
                                                >
                                                    Add Attachment
                                                </StyledButton>
                                            }
                                        </Grid>
                                    </TitleBox>

                                    <Grid
                                        container
                                        size={{
                                            xs: 12
                                        }}
                                        spacing={{
                                            xs: 2
                                        }}
                                    >
                                        <Grid
                                            size={{
                                                xs: 12,
                                                lg: 6
                                            }}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'stretch'
                                            }}
                                        >
                                            <DetailsPanel>
                                                <DetailsHeader>
                                                    Summary
                                                </DetailsHeader>
                                                <DetailsContent>
                                                    <GridColumn>
                                                        <GridRow>
                                                            <GridLabel>
                                                                Status:
                                                            </GridLabel>
                                                            <GridData>
                                                                {GetFieldLookupItem(lotLookups ,"Status", lotData?.status)?.value?.toString()}
                                                            </GridData>
                                                        </GridRow>
                                                        <GridRow>
                                                            <GridLabel>
                                                                Price:
                                                            </GridLabel>
                                                            <GridData>
                                                                {
                                                                    lotData?.price ? Intl.NumberFormat("en-AU", {
                                                                        style: "currency",
                                                                        currency: "AUD"
                                                                    }).format(lotData?.price) : undefined
                                                                }
                                                            </GridData>
                                                        </GridRow>
                                                        <GridRow>
                                                            <GridLabel>
                                                                Release Date:
                                                            </GridLabel>
                                                            <GridData>
                                                                {
                                                                    lotData?.releaseDate 
                                                                        ? DateTime.fromISO(lotData.releaseDate.toString()).toFormat("dd/MM/yyyy")
                                                                        : ""
                                                                }
                                                            </GridData>
                                                        </GridRow>
                                                        <GridFillerRow />
                                                    </GridColumn>
                                                </DetailsContent>
                                            </DetailsPanel>
                                        </Grid>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                lg: 6
                                            }}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'stretch'
                                            }}
                                        >
                                            <DetailsPanel>
                                                <DetailsHeader>
                                                    Lot Properties
                                                </DetailsHeader>
                                                <DetailsContent>
                                                    <GridColumn>
                                                        {
                                                            lotData?.properties?.map((property, index) => 
                                                                <GridRow>
                                                                    <GridLabel
                                                                        sx={{
                                                                            paddingTop: theme.spacing(1)
                                                                        }}
                                                                    >
                                                                        {property.name}
                                                                    </GridLabel>
                                                                    <GridData
                                                                        sx={{
                                                                            marginTop: theme.spacing(1),
                                                                            marginBottom: index + 1 == lotData?.properties?.length ? theme.spacing(1) : 0,
                                                                            display: "flex",
                                                                            width: "100%",
                                                                            alignItems: "flex-start",
                                                                            justifyContent: "space-between"
                                                                        }}
                                                                    >
                                                                        {property.value ?? <Box />}
                                                                        {
                                                                            permissions?.includes(Permissions.LotCanEditProperty) &&
                                                                            <Box sx={{
                                                                                paddingLeft: 2,
                                                                                paddingRight: 2
                                                                            }}>
                                                                                <StyledActionButton 
                                                                                    variant="edit"
                                                                                    to={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/properties/edit/${property.id}`}
                                                                                />
                                                                            </Box>
                                                                        }
                                                                    </GridData>
                                                                </GridRow>
                                                            )
                                                        }
                                                        <GridFillerRow />
                                                    </GridColumn>
                                                </DetailsContent>
                                            </DetailsPanel>
                                        </Grid>
                                    </Grid>

                                    <Box
                                        sx={{
                                            marginTop: theme.spacing(2),
                                            display: 'flex',
                                            flexDirection: 'column',
                                            [`& .${gridClasses.root}`]: {

                                                [`& .${gridClasses.columnHeader}`]: {
                                                    height: 'auto !important',
                                                    minHeight: 56,
                                                
                                                    [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                                                        whiteSpace: 'normal',
                                                
                                                        [`& .${gridClasses.columnHeaderTitle}`]: {
                                                            whiteSpace: 'normal',
                                                            paddingTop: theme.spacing(1.5),
                                                            paddingBottom: theme.spacing(1.5) 
                                                        }
                                                    },
                                                
                                                    [`& .${gridClasses.iconButtonContainer}`]: {
                                                        width: 'unset',
                                                    },

                                                '&.column-group-header': {
                                                    backgroundColor: theme.palette.grey[300],
                                                    boxShadow: "1",
                                                    textAlign: 'center'
                                                }
                                            }
                                        },
                                    }}
                                >
                                    <CollapsibleTablePremium
                                        density="compact"
                                        startCollapsed={false} 
                                        title={"Contracts"}
                                        loading={contractSummariesLoading} 
                                        columns={contractColumns}
                                        rows={contractSummaries}
                                        disableColumnMenu={false}
                                        disableColumnFilter={false}
                                        {...contractsCustomToolbarProps}
                                    />
                                </Box>

                                    <Box
                                        sx={{
                                            marginTop: theme.spacing(2),
                                            [`& .${gridClasses.root}`]: {

                                                [`& .${gridClasses.columnHeader}`]: {
                                                    height: 'auto !important',
                                                    minHeight: 56,
                                                
                                                    [`& .${gridClasses.columnHeaderTitleContainer}`]: {
                                                        whiteSpace: 'normal',
                                                
                                                        [`& .${gridClasses.columnHeaderTitle}`]: {
                                                            whiteSpace: 'normal',
                                                            paddingTop: theme.spacing(1.5),
                                                            paddingBottom: theme.spacing(1.5) 
                                                        }
                                                    },
                                                
                                                    [`& .${gridClasses.iconButtonContainer}`]: {
                                                        width: 'unset',
                                                    },

                                                    '&.column-group-header': {
                                                        backgroundColor: theme.palette.grey[300],
                                                        boxShadow: "1",
                                                        textAlign: 'center'
                                                    }
                                                }
                                            },
                                        }}
                                    >
                                        <CollapsibleTablePremium
                                            density="compact"
                                            startCollapsed={false} 
                                            title={"Notes"} 
                                            loading={lotNotesLoading} 
                                            columns={lotNoteColumns}
                                            rows={lotNotes}
                                            disableColumnMenu={false}
                                            disableColumnFilter={false}
                                            {...notesCustomToolbarProps}
                                        />
                                    </Box>
                                    
                                    <Paper>
                                        <List
                                            disablePadding
                                        >
                                            <LotHistory lotId={lotId} />
                                        </List>
                                    </Paper>
                                    <LotAttachments lotId={lotId}/>
                                </ContractSummariesContext.Provider>
                            </LotAttachmentsContext.Provider>
                        </LotNotesContext.Provider>
                    </LotPropertiesContext.Provider>
                </LotChangesContext.Provider>
            </LotDetailsContext.Provider>
        </>
    );
}